import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PreloadImage from "@MasterBluePrint/Common/Hooks/preLoadImages";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    image: ImageModel;
    useLargeImage?: boolean;
    onClick?: () => void;
    extendedStyles?: ProductPrimaryImageStyles;
    displayViewAllImageLable?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export interface ProductPrimaryImageStyles {
    centeringWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    hiddenWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    spinner?: LoadingSpinnerProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    videoWrapper?: InjectableCss;
    ViewAllLable?: InjectableCss;
}

export const productPrimaryImageStyles: ProductPrimaryImageStyles = {
    centeringWrapper: {
        css: css`
            min-height: 18.75rem;
            max-height: 25rem;
            margin:0;
            text-align: center;
            border:1px solid #ECECEC;
            -webkit-border-radius: .125rem;
            -moz-border-radius: .125rem;
            border-radius: .125rem;
            display: flex;
            justify-content: center;
            align-items: center;
            overflow: hidden;
            position: relative;

            &:hover {
                .viewall-img {
                    display: block;
                }
            }

            img {
                height: auto;
                max-width: 100%;
                max-height: 25rem;
            }
        `,
    },
    image: {
        css: css`
            margin: auto;
            cursor: pointer;
        `,
    },
    img: {
        css: css`
            cursor: pointer;
        `,
    },
    videoWrapper: {
        css: css`
            position: relative;
            iframe {
                position: relative;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                max-width: 100%;
                max-height: 400px;

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                                max-height: 320px;
                            `,
                        null,
                        null,
                    ],
                    "max",
                )
            };
            }
        `,
    },
    ViewAllLable: {
        css: css`
            border: 1px solid #005CB9;
            border-radius: 2px;
            background-color: #005CB9;
            color: #fff;
            padding: 8px 25px 7px 30px;
            font-family: Roboto, sans-serif;
            font-size: 14px;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 21px;
            text-align: center;
            position: absolute;
            top: auto;
            bottom: 30%;
            left: auto;
            z-index: 9;
            cursor: pointer;
            display: none;

            span {
                display: inline-block;
                vertical-align: middle;
                margin-left: 5px;
            }
            
            svg {
                height: 24px;
                width: 24px;
                color: #fff;
            }
        `,
    }
};

const ProductPrimaryImage = ({ productSettings, product, image, useLargeImage, onClick, extendedStyles, displayViewAllImageLable }: Props) => {
    const [styles] = useState(() => mergeToNew(productPrimaryImageStyles, extendedStyles));

    if (!product) {
        return null;
    }

    useEffect(() => {
        if ((window as any).sirvScriptAdded) {
            return;
        }

        if (
            productSettings.imageProvider !== "SIRV" ||
            !product.images ||
            product.images.every(o => o.imageType !== "360")
        ) {
            return;
        }

        const script = document.createElement("script");
        script.src = "https://scripts.sirv.com/sirv.js";
        script.async = true;

        document.body.appendChild(script);
        (window as any).sirvScriptAdded = true;
    }, [productSettings.imageProvider, product]);

    const imageWrapperClickHandler = () => {
        onClick?.();
    };

    const imageWrapperKeyHandler: React.KeyboardEventHandler<HTMLImageElement> = event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Enter") {
            onClick?.();
        }
    };

    const path = useLargeImage
        ? image.largeImagePath || image.mediumImagePath
        : image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }
    const isPathOfIframe = path.startsWith("<iframe");

    return (
        <>
            <PreloadImage imageUrl={path} />
            {image.imageType === "Static" && (
                <StyledWrapper {...styles.centeringWrapper} onClick={imageWrapperClickHandler}>
                    {isPathOfIframe && (
                        <StyledWrapper
                            {...styles.videoWrapper}
                            dangerouslySetInnerHTML={{ __html: path }}
                        ></StyledWrapper>
                    )}
                    {!isPathOfIframe && (
                        <Img
                            {...styles.img}
                            src={path}
                            altText={image.imageAltText}
                            loading="eager"
                            data-test-selector="productDetails_mainImage"
                            onKeyPress={imageWrapperKeyHandler}
                            tabIndex={0}
                        />
                    )}
                    {displayViewAllImageLable && (
                        <Typography className="viewall-img" {...styles.ViewAllLable}>{translate("View all images")} <Icon
                            src="ZoomIn"
                            css={css`
                            height: 1.5rem;
                            width: 1.5rem;                                                 
                        `}
                        /></Typography>
                    )}
                </StyledWrapper>
            )}
            {image.imageType === "360" && productSettings.imageProvider === "SIRV" && (
                <div style={{ minHeight: 300 }} onClick={imageWrapperClickHandler}>
                    <div className="Sirv" key={image.id} data-src={image.mediumImagePath} />
                </div>
            )}
        </>
    );
};

export default connect(mapStateToProps)(ProductPrimaryImage);